import { getSupabaseConfigurationError, supabase, uploadToSupabase } from "@/lib/supabase";
import { compressImageFile, validateImageFile } from "@/lib/adminImageUtils";
import {
  createClientGalleryAlbum,
  deleteClientGalleryAlbum,
  importClientGalleryAlbumDriveFolder,
  importClientGalleryDriveFolder,
  listClientGalleryAlbums,
  updateClientGalleryAlbum,
} from "@/lib/galleryApi";

export function normalizeClientGalleryImages(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(normalizeImageUrl).filter(Boolean);
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
      try {
        return normalizeClientGalleryImages(JSON.parse(trimmed));
      } catch {
        return [trimmed];
      }
    }
    return [trimmed];
  }
  if (typeof value === "object") {
    return Object.values(value).map(normalizeImageUrl).filter(Boolean);
  }
  return [];
}

export function normalizeImageUrl(value) {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  return String(value.url || value.src || value.image || value.path || "").trim();
}

export function slugifyGallery(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export function generateShareToken() {
  const bytes = new Uint8Array(18);
  window.crypto?.getRandomValues?.(bytes);
  const random = [...bytes].map((byte) => byte.toString(36).padStart(2, "0")).join("");
  return `mff-${random.slice(0, 28)}`;
}

export const DEFAULT_CLIENT_GALLERY_COVER_FRAME = {
  coverPositionX: 50,
  coverPositionY: 36,
  coverScale: 1.08,
};

function clampNumber(value, min, max, fallback) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(min, number));
}

export function normalizeClientGalleryCoverFrame(value) {
  return {
    coverPositionX: clampNumber(
      value?.coverPositionX ?? value?.cover_position_x,
      0,
      100,
      DEFAULT_CLIENT_GALLERY_COVER_FRAME.coverPositionX
    ),
    coverPositionY: clampNumber(
      value?.coverPositionY ?? value?.cover_position_y,
      0,
      100,
      DEFAULT_CLIENT_GALLERY_COVER_FRAME.coverPositionY
    ),
    coverScale: clampNumber(
      value?.coverScale ?? value?.cover_scale,
      1,
      1.8,
      DEFAULT_CLIENT_GALLERY_COVER_FRAME.coverScale
    ),
  };
}

function requireSupabase() {
  if (!supabase) throw new Error(getSupabaseConfigurationError() || "Supabase is not configured");
  return supabase;
}

function rpcError(error, fallback) {
  const message = String(error?.message || "");
  if (message.includes("GALLERY_INACTIVE")) return new Error("Gallery inactive.");
  if (message.includes("GALLERY_EXPIRED")) return new Error("Gallery expired.");
  if (message.includes("INVALID_GALLERY_ACCESS")) return new Error("Invalid gallery name/code or password.");
  if (message.includes("CLIENT_GALLERY_ADMIN_ONLY")) return new Error("Only the configured admin can manage client galleries.");
  if (message.includes("CLIENT_GALLERY_PIN_INVALID")) return new Error("Password/PIN must be 4-8 letters or digits.");
  if (message.includes("duplicate key") || error?.code === "23505") return new Error("Gallery slug or private share token already exists.");
  return new Error(message || fallback);
}

export function mapClientGalleryRow(row) {
  if (!row) return null;
  const gallery = normalizeClientGalleryImages(row.gallery);
  const albums = Array.isArray(row.albums) ? row.albums.map((album) => ({
    id: album.id,
    title: album.title || album.folder_name || album.folderName || "Album",
    driveFolderId: album.drive_folder_id || album.driveFolderId || "",
    driveFolderUrl: album.drive_folder_url || album.driveFolderUrl || "",
    coverPhotoId: album.cover_photo_id || album.coverPhotoId || "",
    coverImage: album.cover_image_url || album.coverImage || "",
    sortOrder: Number(album.sort_order ?? album.display_order ?? album.displayOrder ?? 0),
    isActive: album.is_active !== false && album.isActive !== false,
    photoCount: Number(album.photo_count ?? album.photoCount ?? 0),
  })) : [];
  return {
    id: row.id || row.event_id,
    title: row.title || row.event_name || "",
    slug: row.slug || "",
    shareToken: row.share_token || row.token || "",
    accessPassword: row.access_password || row.pin_code || "",
    coverImage: row.cover_image || "",
    ...normalizeClientGalleryCoverFrame(row),
    driveFolderId: row.drive_folder_id || row.google_drive_folder_id || "",
    driveFolderUrl: row.drive_folder_url || "",
    gallery,
    status: row.status || (row.active === false || row.is_active === false ? "inactive" : "active"),
    expiresAt: row.expires_at || "",
    downloadAllowed: Boolean(row.download_allowed ?? row.download_enabled),
    createdAt: row.created_at || "",
    updatedAt: row.updated_at || "",
    photoCount: Number(row.photo_count ?? gallery.length),
    albums,
  };
}

export async function verifySecureClientGallery({ galleryCode, password, shareToken }) {
  const client = requireSupabase();
  const { data, error } = await client.rpc("verify_client_gallery_access", {
    p_gallery_code: String(galleryCode || "").trim() || null,
    p_password: String(password || "").trim(),
    p_share_token: String(shareToken || "").trim() || null,
  });
  if (error) throw rpcError(error, "Invalid gallery name/code or password.");
  const gallery = mapClientGalleryRow(Array.isArray(data) ? data[0] : data);
  if (!gallery?.id) throw new Error("Invalid gallery name/code or password.");
  return gallery;
}

export async function listVerifiedClientGalleryPhotos({ eventId, shareToken, password, albumId = null, limit = 60, offset = 0 }) {
  const client = requireSupabase();
  const params = {
    p_event_id: eventId,
    p_share_token: String(shareToken || "").trim(),
    p_password: String(password || "").trim(),
    p_limit: Math.min(Math.max(Number(limit) || 60, 1), 100),
    p_offset: Math.max(Number(offset) || 0, 0),
    p_album_id: albumId || null,
  };
  let { data, error } = await client.rpc("get_verified_client_gallery_photos", params);
  if (error?.code === "PGRST202" && !albumId) {
    const legacyParams = { ...params };
    delete legacyParams.p_album_id;
    ({ data, error } = await client.rpc("get_verified_client_gallery_photos", legacyParams));
  }
  if (error) {
    if (error.code === "PGRST202" || String(error.message || "").includes("get_verified_client_gallery_photos")) {
      return { photos: [], total: 0, migrationRequired: true };
    }
    throw rpcError(error, "Could not load gallery photos.");
  }
  const rows = data || [];
  return {
    total: Number(rows[0]?.total_count || 0),
    photos: rows.map((row) => {
      const mimeType = row.mime_type || "image/jpeg";
      const mediaType = row.media_type || (String(mimeType).startsWith("video/") ? "video" : "image");
      const driveFileId = row.drive_file_id || row.file_id || row.google_drive_file_id || "";
      return {
        id: row.id,
        driveFileId,
        albumId: row.album_id || row.folder_id || "",
        fileName: row.file_name || (mediaType === "video" ? "Video" : "Photo"),
        mimeType,
        mediaType,
        thumbnailUrl: row.thumbnail_url || row.preview_url || row.original_url || "",
        previewUrl: row.preview_url || row.original_url || row.thumbnail_url || "",
        originalUrl: row.original_url || row.preview_url || row.thumbnail_url || "",
        downloadUrl: row.download_url || (driveFileId ? `https://drive.google.com/uc?export=download&id=${encodeURIComponent(driveFileId)}` : ""),
        imageUrl: row.image_url || "",
        width: Number(row.width || 0),
        height: Number(row.height || 0),
        duration: Number(row.duration || 0),
        sizeBytes: Number(row.size_bytes || 0),
        orderIndex: Number(row.order_index || 0),
      };
    }).filter((photo) => photo.thumbnailUrl || photo.previewUrl),
    migrationRequired: false,
  };
}

export async function importDriveFolderMetadata(eventId, folderUrlOrId) {
  return withAdminBackendSession((accessToken) => importClientGalleryDriveFolder(eventId, folderUrlOrId, accessToken));
}

function isBackendAdminAuthError(error) {
  return /admin authentication required|admin session expired|invalid jwt|jwt expired/i.test(String(error?.message || ""));
}

async function adminAccessToken({ forceRefresh = false } = {}) {
  const client = requireSupabase();
  const { data, error } = forceRefresh
    ? await client.auth.refreshSession()
    : await client.auth.getSession();
  const accessToken = data?.session?.access_token;
  if (error || !accessToken) throw new Error("Admin session expired. Please sign in again.");
  return accessToken;
}

async function withAdminBackendSession(operation) {
  try {
    return await operation(await adminAccessToken());
  } catch (error) {
    if (!isBackendAdminAuthError(error)) throw error;
  }

  try {
    return await operation(await adminAccessToken({ forceRefresh: true }));
  } catch (error) {
    if (isBackendAdminAuthError(error)) {
      throw new Error("Admin session could not be verified by backend. Please sign out, sign in again, and restart backend with the same Supabase project settings.");
    }
    throw error;
  }
}

export async function listSecureClientGalleryAlbums(eventId) {
  const data = await withAdminBackendSession((accessToken) => listClientGalleryAlbums(eventId, accessToken));
  return (data.albums || []).map((album) => ({
    id: album.id,
    title: album.folderName || album.title || "Album",
    driveFolderId: album.driveFolderId || "",
    driveFolderUrl: album.driveFolderUrl || "",
    coverPhotoId: album.coverPhotoId || "",
    coverImage: album.coverImage || "",
    sortOrder: Number(album.displayOrder || 0),
    isActive: album.isActive !== false,
    photoCount: Number(album.photoCount || 0),
  }));
}

export async function createSecureClientGalleryAlbum(eventId, payload) {
  const data = await withAdminBackendSession((accessToken) => createClientGalleryAlbum(eventId, payload, accessToken));
  return data.album;
}

export async function updateSecureClientGalleryAlbum(eventId, albumId, payload) {
  const data = await withAdminBackendSession((accessToken) => updateClientGalleryAlbum(eventId, albumId, payload, accessToken));
  return data.album;
}

export async function deleteSecureClientGalleryAlbum(eventId, albumId) {
  await withAdminBackendSession((accessToken) => deleteClientGalleryAlbum(eventId, albumId, accessToken));
}

export async function importSecureClientGalleryAlbum(eventId, albumId, folderUrlOrId) {
  return withAdminBackendSession((accessToken) => importClientGalleryAlbumDriveFolder(eventId, albumId, folderUrlOrId, accessToken));
}

export async function listSecureClientGalleries() {
  const client = requireSupabase();
  const { data, error } = await client.rpc("admin_list_client_galleries");
  if (error) throw rpcError(error, "Could not load client galleries.");
  return (data || []).map(mapClientGalleryRow);
}

export async function saveSecureClientGallery(form) {
  const client = requireSupabase();
  const title = String(form.title || "").trim();
  const slug = slugifyGallery(form.slug || title);
  const shareToken = String(form.shareToken || "").trim() || generateShareToken();
  const pin = String(form.accessPassword || "").trim();
  if (!title) throw new Error("Client/Event name is required.");
  if (!slug) throw new Error("Gallery slug/code is required.");
  if (!/^[A-Za-z0-9]{4,8}$/.test(pin)) throw new Error("Password/PIN must be 4-8 letters or digits.");

  const coverFrame = normalizeClientGalleryCoverFrame(form);
  const legacyParams = {
    p_event_id: form.id || null,
    p_title: title,
    p_slug: slug,
    p_share_token: shareToken,
    p_pin_code: pin,
    p_is_active: form.status !== "inactive",
    p_expires_at: form.expiresAt || null,
    p_download_allowed: Boolean(form.downloadAllowed),
    p_cover_image: String(form.coverImage || "").trim() || null,
    p_gallery: normalizeClientGalleryImages(form.gallery),
  };
  const nextParams = {
    ...legacyParams,
    p_cover_position_x: coverFrame.coverPositionX,
    p_cover_position_y: coverFrame.coverPositionY,
    p_cover_scale: coverFrame.coverScale,
  };
  let { data, error } = await client.rpc("admin_save_client_gallery", nextParams);
  if (error?.code === "PGRST202" || String(error?.message || "").includes("p_cover_position")) {
    const changedFrame = coverFrame.coverPositionX !== DEFAULT_CLIENT_GALLERY_COVER_FRAME.coverPositionX
      || coverFrame.coverPositionY !== DEFAULT_CLIENT_GALLERY_COVER_FRAME.coverPositionY
      || coverFrame.coverScale !== DEFAULT_CLIENT_GALLERY_COVER_FRAME.coverScale;
    if (changedFrame) {
      throw new Error("Run supabase-client-gallery-cover-framing.sql before saving custom hero banner framing.");
    }
    ({ data, error } = await client.rpc("admin_save_client_gallery", legacyParams));
  }
  if (error) throw rpcError(error, "Could not save client gallery.");
  return mapClientGalleryRow(Array.isArray(data) ? data[0] : data);
}

export async function deleteSecureClientGallery(id) {
  const client = requireSupabase();
  const { error } = await client.rpc("admin_delete_client_gallery", { p_event_id: id });
  if (error) throw rpcError(error, "Could not delete client gallery.");
  return true;
}

export async function uploadClientGalleryImage(file, folder = "client-galleries") {
  validateImageFile(file);
  const optimized = await compressImageFile(file, { maxEdge: 2000, quality: 0.84 });
  return uploadToSupabase(optimized, folder);
}
